import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react'
import { SocketEvent } from '~/constants/socket'
import { fetchWrapper } from '~/utils/fetchWrapper'
import { AppEvents, globalEventEmitter } from '~/utils/shared'

// Painted into the checkout tab the instant it opens, so it shows a
// "Redirecting to checkout…" state instead of a blank window while the Stripe
// session is being created. Replaced when the tab navigates to Stripe.
const CHECKOUT_LOADING_HTML = `<!doctype html><html><head><meta charset="utf-8"><title>Redirecting to checkout…</title></head><body style="margin:0;height:100vh;display:flex;align-items:center;justify-content:center;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;color:#555;background:#fff"><div style="text-align:center"><div style="width:28px;height:28px;margin:0 auto 12px;border:3px solid #e5e5e5;border-top-color:#888;border-radius:50%;animation:s .8s linear infinite"></div>Redirecting to checkout…</div><style>@keyframes s{to{transform:rotate(360deg)}}</style></body></html>`

export type CheckoutStatus = 'idle' | 'pending' | 'verifying' | 'waitingForLicense'

interface StripeCheckoutCallbacks {
  billingId: string | number | undefined
  onSuccess: () => void
  onFailure: () => void
}

interface StripeCheckoutContextValue {
  isLoading: boolean
  checkoutStatus: CheckoutStatus
  /**
   * Open the Stripe checkout tab. Must be called synchronously from the click
   * (do NOT await anything before it) so the tab opens inside the user
   * activation and isn't popup-blocked. The checkout-session create runs via
   * `getRedirectUrl` AFTER the tab is open; the tab then navigates to the URL.
   * Returns false only if the browser blocked the tab; rejects (so the caller
   * can show a specific error) if `getRedirectUrl` throws.
   */
  startCheckout: (getRedirectUrl: () => Promise<string>) => Promise<boolean>
  cleanup: () => void
  /** Register callbacks for the active checkout consumer. Pass null to unregister. */
  register: (callbacks: StripeCheckoutCallbacks | null) => void
}

const StripeCheckoutContext = createContext<StripeCheckoutContextValue | null>(null)

interface StripeCheckoutProviderProps {
  children: ReactNode
}

/**
 * Single Stripe checkout provider. All dialogs (PlanDialog, ZeroCreditsDialog,
 * ManageAccountDialog) share this instance — no duplicate listeners or toasts.
 */
export function StripeCheckoutProvider({ children }: StripeCheckoutProviderProps) {
  const [isLoading, setIsLoading] = useState(false)
  const [checkoutStatus, setCheckoutStatus] = useState<CheckoutStatus>('idle')

  const checkoutTabRef = useRef<Window | null>(null)
  const pollIntervalRef = useRef<number | null>(null)
  const sessionIdRef = useRef<string | null>(null)
  const licenseUpdatedReceivedRef = useRef(false)
  const verificationDoneRef = useRef(false)

  const callbacksRef = useRef<StripeCheckoutCallbacks | null>(null)

  const register = useCallback((callbacks: StripeCheckoutCallbacks | null) => {
    callbacksRef.current = callbacks
  }, [])

  const handleSuccess = useCallback(() => {
    setIsLoading(false)
    setCheckoutStatus('idle')
    callbacksRef.current?.onSuccess()
  }, [])

  const handleFailure = useCallback(() => {
    setIsLoading(false)
    setCheckoutStatus('idle')
    callbacksRef.current?.onFailure()
  }, [])

  const verifyCheckoutSession = useCallback(
    async (sessionId: string) => {
      const billingId = callbacksRef.current?.billingId
      if (!billingId) return false
      if (verificationDoneRef.current) return true

      try {
        setCheckoutStatus('verifying')
        const response = await fetchWrapper(
          `/api/billing/${billingId}/verify-subscription/${sessionId}`,
          { method: 'GET' }
        )

        if (response.ok) {
          const data = await response.json().catch(() => ({}))

          if (data.payment_type === 'flex_activation' || data.payment_type === 'credit_purchase') {
            if (!verificationDoneRef.current) {
              verificationDoneRef.current = true
              handleSuccess()
            }
            return true
          }

          if (licenseUpdatedReceivedRef.current) {
            licenseUpdatedReceivedRef.current = false
            if (!verificationDoneRef.current) {
              verificationDoneRef.current = true
              handleSuccess()
            }
          } else {
            setCheckoutStatus('waitingForLicense')
          }
          return true
        }
        return false
      } catch {
        return false
      }
    },
    [handleSuccess]
  )

  useEffect(() => {
    if (
      checkoutStatus !== 'pending' &&
      checkoutStatus !== 'verifying' &&
      checkoutStatus !== 'waitingForLicense'
    )
      return

    const onSocketEvent = (...args: unknown[]) => {
      const data = args[0] as { event: string }
      if (data.event === SocketEvent.LicenseUpdated) {
        if (checkoutStatus === 'waitingForLicense') {
          handleSuccess()
        } else {
          licenseUpdatedReceivedRef.current = true
        }
      }
    }

    globalEventEmitter.on(AppEvents.SocketEvent, onSocketEvent)

    let timeout: number | undefined
    if (checkoutStatus === 'waitingForLicense') {
      timeout = window.setTimeout(() => handleSuccess(), 30000)
    }

    return () => {
      globalEventEmitter.off(AppEvents.SocketEvent, onSocketEvent)
      if (timeout) clearTimeout(timeout)
    }
  }, [checkoutStatus, handleSuccess])

  useEffect(() => {
    if (checkoutStatus !== 'pending' || !checkoutTabRef.current) return

    pollIntervalRef.current = window.setInterval(async () => {
      const tab = checkoutTabRef.current

      if (!tab || tab.closed) {
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current)
          pollIntervalRef.current = null
        }

        if (!verificationDoneRef.current) {
          const sessionId = sessionIdRef.current

          if (sessionId) {
            const verified = await verifyCheckoutSession(sessionId)
            if (!verified && !verificationDoneRef.current) {
              handleFailure()
            }
          } else {
            handleFailure()
          }
        }

        checkoutTabRef.current = null
      }
    }, 1000)

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current)
        pollIntervalRef.current = null
      }
    }
  }, [checkoutStatus, verifyCheckoutSession, handleFailure])

  useEffect(() => {
    const handleMessage = async (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return

      if (event.data?.type === 'stripe-checkout-complete' && event.data?.sessionId) {
        sessionIdRef.current = event.data.sessionId

        const verified = await verifyCheckoutSession(event.data.sessionId)
        if (!verified && !verificationDoneRef.current) {
          handleFailure()
        }
      } else if (event.data?.type === 'stripe-checkout-canceled') {
        handleFailure()
      }
    }

    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [verifyCheckoutSession, handleFailure])

  const startCheckout = useCallback(
    async (getRedirectUrl: () => Promise<string>): Promise<boolean> => {
      sessionIdRef.current = null
      licenseUpdatedReceivedRef.current = false
      verificationDoneRef.current = false
      setIsLoading(true)

      // Open the tab SYNCHRONOUSLY (in the click's user activation) so the
      // browser doesn't block it. The checkout-session create runs afterwards.
      const tab = window.open('about:blank', '_blank')
      if (!tab) {
        setIsLoading(false)
        return false
      }
      try {
        tab.document?.write?.(CHECKOUT_LOADING_HTML)
        tab.document?.close?.()
      } catch {
        // about:blank document quirks — non-fatal.
      }
      checkoutTabRef.current = tab

      let redirectUrl: string
      try {
        redirectUrl = await getRedirectUrl()
      } catch (e) {
        if (!tab.closed) tab.close()
        checkoutTabRef.current = null
        setIsLoading(false)
        throw e // let the caller surface the specific error
      }

      // The user may have closed the placeholder tab while the session was being
      // created — don't navigate a dead window or kick off the poll/verify loop.
      if (tab.closed || !redirectUrl) {
        if (!tab.closed) tab.close()
        checkoutTabRef.current = null
        setIsLoading(false)
        return true
      }

      tab.location.href = redirectUrl
      // Flip to 'pending' only now — this starts the poll/verify/license-wait
      // machine, which must run only once the real checkout is underway.
      setCheckoutStatus('pending')
      return true
    },
    []
  )

  const cleanup = useCallback(() => {
    if (checkoutTabRef.current) {
      checkoutTabRef.current.close()
      checkoutTabRef.current = null
    }
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current)
      pollIntervalRef.current = null
    }
    setIsLoading(false)
    setCheckoutStatus('idle')
  }, [])

  const value: StripeCheckoutContextValue = {
    isLoading,
    checkoutStatus,
    startCheckout,
    cleanup,
    register
  }

  return <StripeCheckoutContext.Provider value={value}>{children}</StripeCheckoutContext.Provider>
}

export function useStripeCheckoutContext(): StripeCheckoutContextValue {
  const context = useContext(StripeCheckoutContext)
  if (!context) {
    throw new Error('useStripeCheckoutContext must be used within StripeCheckoutProvider')
  }
  return context
}
